import { useEffect, useRef, useState, type ReactNode } from "react";
import { AUTH, BRAND, VERIFY_EMAIL } from "../design/copy.js";
import { BloomBackground } from "../ui/background.js";
import { Pill, Rule } from "../ui/primitives.js";
import { isSpentLink, isTooManyTries } from "../data/auth-errors.js";
import { accountRefusalWords, focusDropped, useAccountSend } from "../data/account-send.js";

/** The two requests the page can make, named here for the reason check-in.tsx gives. */
export interface VerifyEmailClient {
  readonly verifyEmail: (token: string) => Promise<unknown>;
  readonly sendVerificationEmail: (email: string) => Promise<unknown>;
}

type Phase = "ready" | "sending" | "already" | "spent" | "notThrough" | "tooMany";

/**
 * The address a verification link was sent to, read from the token, or ""
 * when it cannot be read. better-auth's verification token is an HS256 JWT
 * whose payload is `{email}` as UTF-8 JSON; nothing is verified here (the
 * server does that). Two uses, both in this page only: the line that names
 * the address the button would confirm (review, 2026-10-22: a link someone
 * else sent would otherwise sign this browser in to their account with no
 * word of whose), and the "send a new link." field, which stays editable.
 * Read in the browser and sent nowhere. A token that is not a JWT gives "".
 */
export function emailFromVerificationToken(token: string): string {
  const payload = token.split(".")[1];
  if (payload === undefined || payload.length === 0) return "";
  try {
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const binary = atob(base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "="));
    const text = new TextDecoder("utf-8", { fatal: true }).decode(Uint8Array.from(binary, (c) => c.charCodeAt(0)));
    const parsed: unknown = JSON.parse(text);
    if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) return "";
    const email: unknown = (parsed as Record<string, unknown>)["email"];
    return typeof email === "string" ? email : "";
  } catch {
    return "";
  }
}

/**
 * THE VERIFY PAGE (`#/verify?token=`, 2026-10-22, hard verification, ruling
 * 8): where the verification email's link lands. One heading, one sentence,
 * the address the link confirms (read from the token in the browser, the
 * review of 2026-10-22, with "only confirm if this is your address" and what
 * to do if the account isn't yours), one button, and nothing on arrival: a
 * link prefetcher or a mail scanner that opens the address confirms nothing
 * and signs nobody in, because only the button sends the token (`GET
 * /auth/verify-email`, no callbackURL, the client's own header). The
 * unsubscribe page's precedent.
 *
 * The first confirmation signs this browser in (`autoSignInAfterVerification`);
 * the app then asks who this is, which is the request on which a guest's
 * work moves into the account when this browser holds the guest cookie, and
 * goes home (`onVerified` answers true). better-auth answers an address
 * already confirmed the same way but signs nobody in, so when the app finds
 * nobody signed in (`onVerified` answers false) the page says the address
 * was confirmed before and offers "sign in.".
 *
 * A spent link (expired, used, not one of ours, or its account gone) is said
 * once, with no button, and a form for a new link in its place, the address
 * read from the token. A request the server did not answer may be tried
 * again. One live region, present from the first render, around everything
 * that changes; focus moves to the sentence that replaced the button, and to
 * what "send a new link." came to when its door goes to rest (the pre-release
 * review, 2026-10-22) or, refused, comes back with focus dropped to the page
 * (round two): to the address field when the address is incomplete, else to
 * what was said.
 *
 * The link stays in the address until the server has answered in a way that
 * settles it (round two, 2026-10-22): confirmed, confirmed before, or spent.
 * Then `onLinkSettled` lets the app take the token out of the history entry,
 * before it goes home. A request not answered, or a limit, keeps it: the
 * link may still work, and "Open in Safari" from a mail app carries it.
 * Claude's placeholder words, in VERIFY_EMAIL; the privacy card's layout.
 */
export function VerifyEmailPage({
  client,
  token,
  onVerified,
  onSignIn,
  onLinkSettled,
  guestKnown = false,
}: {
  client: VerifyEmailClient;
  token: string | null;
  /** Asks who this is after the confirmation; true when an account is now signed in (the app goes home). */
  onVerified: () => Promise<boolean>;
  onSignIn: () => void;
  /** The server's answer has settled this token (round two, 2026-10-22): the app takes it out of the address. */
  onLinkSettled: (token: string) => void;
  /** True when this browser holds a guest, whose work comes with the sign-in. */
  guestKnown?: boolean;
}): ReactNode {
  const [phase, setPhase] = useState<Phase>(token === null ? "spent" : "ready");
  const [address, setAddress] = useState(() => (token === null ? "" : emailFromVerificationToken(token)));
  const sent = useRef(false);
  const resend = useAccountSend();
  const sentenceRef = useRef<HTMLParagraphElement>(null);
  const moved = useRef(false);
  useEffect(() => {
    // Focus follows a press, never a cold arrival.
    if (moved.current && (phase === "already" || phase === "spent")) sentenceRef.current?.focus();
  }, [phase]);
  const resentRef = useRef<HTMLParagraphElement>(null);
  const resendFieldRef = useRef<HTMLInputElement>(null);
  /** Set by a press of "send a new link.", so focus follows that door's answer. */
  const focusResent = useRef(false);
  useEffect(() => {
    if (!focusResent.current || resend.phase === "sending") return;
    focusResent.current = false;
    // Focus that never dropped (Enter in the field) stays where it is.
    if (!focusDropped()) return;
    // Resting, the pressed button is gone from under the keyboard; refused (round two, 2026-10-22), it
    // was disabled while it sent and the browser let go of it. An incomplete address is corrected in
    // the field; anything else is read where it is said.
    if (resend.phase === "ready" && resend.said === "addressIncomplete") resendFieldRef.current?.focus();
    else resentRef.current?.focus();
  }, [resend.phase, resend.said]);

  const confirm = async (): Promise<void> => {
    if (token === null || sent.current) return;
    sent.current = true;
    moved.current = true;
    setPhase("sending");
    try {
      await client.verifyEmail(token);
    } catch (error) {
      if (isSpentLink(error)) {
        onLinkSettled(token);
        setPhase("spent");
        return;
      }
      // A request that was not answered may be tried again, and the link with it.
      sent.current = false;
      setPhase(isTooManyTries(error) ? "tooMany" : "notThrough");
      return;
    }
    // Confirmed, or confirmed before: the link has done its work, and leaves the address before the app goes home.
    onLinkSettled(token);
    try {
      // On true the app goes home and this page is gone.
      if (!(await onVerified())) setPhase("already");
    } catch {
      sent.current = false;
      setPhase("notThrough");
    }
  };

  const confirmable = phase === "ready" || phase === "sending" || phase === "notThrough" || phase === "tooMany";
  // better-auth's refusals of a signed-in browser's resend are said by what they are (review, 2026-10-22).
  const resendWords =
    resend.said === null ? null : resend.said === "sent" ? VERIFY_EMAIL.resent : resend.said === "notSent" ? VERIFY_EMAIL.notThrough : accountRefusalWords(resend.said);
  /** The address the button would confirm, read from the token; "" when it cannot be read. */
  const linkAddress = token === null ? "" : emailFromVerificationToken(token);

  return (
    <div className="relative flex min-h-svh items-start justify-center sm:py-16">
      <BloomBackground />
      <div data-testid="verify-card" className="relative z-10 w-full bg-paper px-6 py-10 sm:w-[44%] sm:min-w-[34rem] sm:max-w-[46rem] sm:px-16 sm:py-12">
        <div className="font-sans text-nav">{BRAND.short}</div>
        <Rule className="mt-2" />
        <h1 className="mt-6 mb-0 font-display text-display font-bold">{VERIFY_EMAIL.heading}</h1>
        <div aria-live="polite">
          {confirmable ? (
            <>
              <p className="mt-8 mb-0 font-sans text-body">{VERIFY_EMAIL.sentence}</p>
              {linkAddress.length > 0 ? (
                <>
                  <p className="mt-4 mb-0 font-sans text-body font-bold break-words">{VERIFY_EMAIL.forAddress(linkAddress)}</p>
                  <p className="mt-2 mb-0 font-sans text-body">{VERIFY_EMAIL.onlyIfYours}</p>
                </>
              ) : null}
              {guestKnown ? <p className="mt-4 mb-0 font-sans text-body">{VERIFY_EMAIL.guest}</p> : null}
              <p className="mt-4 mb-0 font-sans text-small text-ink-soft">{VERIFY_EMAIL.notYou}</p>
              <div className="mt-8">
                <Pill onClick={() => void confirm()} disabled={phase === "sending"}>
                  {VERIFY_EMAIL.button}
                </Pill>
              </div>
              {phase === "notThrough" ? <p className="mt-4 mb-0 font-sans text-small">{VERIFY_EMAIL.notThrough}</p> : null}
              {phase === "tooMany" ? <p className="mt-4 mb-0 font-sans text-small">{AUTH.tooManyTries}</p> : null}
            </>
          ) : phase === "already" ? (
            <>
              <p ref={sentenceRef} tabIndex={-1} className="mt-8 mb-0 font-sans text-body outline-none">
                {VERIFY_EMAIL.already}
              </p>
              <div className="mt-8">
                <Pill onClick={onSignIn}>{VERIFY_EMAIL.signIn}</Pill>
              </div>
            </>
          ) : (
            <>
              <p ref={sentenceRef} tabIndex={-1} className="mt-8 mb-0 font-sans text-body outline-none">
                {token === null ? VERIFY_EMAIL.badLink : VERIFY_EMAIL.expired}
              </p>
              <form
                className="mt-8 flex flex-col gap-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  const email = address.trim();
                  if (email.length === 0) return;
                  focusResent.current = true;
                  void resend.send(() => client.sendVerificationEmail(email));
                }}
              >
                <p className="m-0 font-sans text-body">{VERIFY_EMAIL.resendIntro}</p>
                <input
                  ref={resendFieldRef}
                  aria-label={AUTH.emailPlaceholder}
                  type="email"
                  autoComplete="email"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder={AUTH.emailPlaceholder}
                  className="w-full border border-ink bg-paper px-4 py-2 font-sans text-body text-ink outline-none placeholder:font-display placeholder:font-bold placeholder:text-ink-faint"
                />
                {resend.phase === "resting" ? null : (
                  <div>
                    <Pill type="submit" disabled={resend.phase === "sending" || address.trim().length === 0}>
                      {VERIFY_EMAIL.resendButton}
                    </Pill>
                  </div>
                )}
              </form>
              {resendWords !== null ? (
                <p ref={resentRef} tabIndex={-1} className="mt-4 mb-0 font-sans text-small outline-none">
                  {resendWords}
                </p>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
